import { Static, Type } from '@sinclair/typebox';
import { MIRRORABLE_EVENT_KEYS } from './taxonomy';
import { VISITOR_ID_MAX_LENGTH } from './visitor-id';

/**
 * Pixel Meta réutilisé de la v1 Cfixé (audiences + apprentissage conservés). ID
 * public — pas un secret. **Source de vérité unique** : la vitrine ET la page
 * register de l'app DOIVENT utiliser le même id pour que la déduplication
 * Pixel ↔ CAPI fonctionne d'un sous-domaine à l'autre. Le backend lit son propre
 * id via `META_CAPI_PIXEL_ID` (env) — garder les deux alignés.
 */
export const META_PIXEL_ID = '944964943333560';

/**
 * Beacon « miroir serveur » (`/public/utils/set-event` — chemin volontairement sans
 * « track » pour passer sous les bloqueurs, comme en v1). Le corps transporte la
 * CLÉ CANONIQUE de la taxonomie, jamais un nom d'event de régie : le backend
 * fan-out vers Meta + TikTok (+ Google, lot 5) en lisant la table.
 *
 * `key` est validé contre l'allowlist DÉRIVÉE du flag `mirrorable` (jamais de
 * `surface`) : un ancien bundle en cache qui envoie encore `{ event_name }` échoue
 * la validation TypeBox et son beacon est droppé — perte assumée, AUCUNE table de
 * correspondance inverse (décision plan §2f). `/set-pv` a été supprimé (code mort) :
 * `page_view` passe par ce beacon comme n'importe quelle clé.
 *
 * `event_id` est généré côté front (cf. `generateEventId`) et partagé avec le
 * `fbq(..., { eventID })` / `ttq.track(..., { event_id })` → chaque régie déduplique
 * navigateur + serveur. `event_ids` porte les overrides par slot (`meta[0]`…).
 */
export const TrackingSetEventRequestSchema = Type.Object({
  key: Type.Union(MIRRORABLE_EVENT_KEYS.map((k) => Type.Literal(k))),
  event_id: Type.String({ minLength: 8, maxLength: 64 }),
  event_ids: Type.Optional(
    Type.Record(Type.String({ maxLength: 20 }), Type.String({ minLength: 8, maxLength: 64 }), { maxProperties: 6 }),
  ),
  event_source_url: Type.String({ maxLength: 2048 }),
  // `false` = copie ANONYME (bannière refusée) : le backend ne garde que les
  // identifiants de clic d'URL, sans IP/UA ni cookies — cf. `ServerMirrorBody`.
  // Absent = consenti (bundles antérieurs à ce champ).
  marketing_consent: Type.Optional(Type.Boolean()),
  // Identifiants transmis par le front pour un matching fort sur les events anonymes :
  // cookies Pixel Meta (`_fbp`/`_fbc`), cookie Pixel TikTok (`_ttp`), `fbclid`/`ttclid`
  // d'URL, `oppref`/`obref` OpenAI.
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  // OpenAI Ads : `oppref` = identifiant de clic (URL d'arrivée, puis cookie
  // `__oppref` posé par le SDK) ; `obref` = référence navigateur (cookie `__obref`,
  // équivalent de `_fbp` — donc soumis au consentement côté client).
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
  // Identifiant visiteur première partie (cookie `propelsia_vid`, cf. `visitor-id.ts`) :
  // devient l'`external_id` de la copie serveur. Copie consentie uniquement.
  visitor_id: Type.Optional(Type.String({ maxLength: VISITOR_ID_MAX_LENGTH })),
  props: Type.Optional(
    Type.Record(
      Type.String({ maxLength: 40 }),
      Type.Union([Type.String({ maxLength: 200 }), Type.Number(), Type.Boolean()]),
      { maxProperties: 8 },
    ),
  ),
});
export type TrackingSetEventRequest = Static<typeof TrackingSetEventRequestSchema>;
